import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from 'api'
import { queryKeys, InvoicesListResponse } from 'api/queryKeys'
import { Invoice } from 'types'

const useDeleteInvoice = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void
  onError?: (error: Error) => void
}) => {
  const client = useApi()
  const queryClient = useQueryClient()

  const { mutate: deleteInvoice, isPending } = useMutation({
    mutationFn: async (invoice: Invoice) => {
      await client.deleteInvoice({ id: Number(invoice.id) })
    },

    // Same optimistic pattern as useUpdateInvoiceFields, but simpler:
    // remove the invoice from the list immediately rather than patching a field.
    onMutate: async (invoice: Invoice) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.invoices.all() })

      const previousLists = queryClient.getQueriesData<InvoicesListResponse>({ queryKey: queryKeys.invoices.all() })

      queryClient.setQueriesData<InvoicesListResponse>(
        { queryKey: queryKeys.invoices.all() },
        (old) => {
          if (!old?.data?.invoices) return old
          return {
            ...old,
            data: {
              ...old.data,
              invoices: old.data.invoices.filter((inv) => inv.id !== invoice.id),
            },
          }
        }
      )

      return { previousLists }
    },

    // Restore all list caches to the pre-delete snapshot.
    // No detail cache to restore — deletion navigates away from the detail page.
    onError: (error, _invoice, context) => {
      context?.previousLists.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      onError?.(error as Error)
    },

    onSuccess,

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all() })
    },
  })

  return { deleteInvoice, isPending }
}

export default useDeleteInvoice
